import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, Heart } from "lucide-react";
import { images } from "../assets/images";

export function Footer() {
  const footerLinks = {
    about: {
      title: "关于我们",
      links: [
        { name: "项目介绍", href: "#about" },
        { name: "团队成员", href: "#team" },
        { name: "我们的使命", href: "#mission" },
        { name: "年度报告", href: "#report" },
      ],
    },
    action: {
      title: "白鸽行动",
      links: [
        { name: "救援计划", href: "#rescue" },
        { name: "疗愈项目", href: "#healing" },
        { name: "教育支持", href: "#education" },
        { name: "成功案例", href: "#cases" },
      ],
    },
    participate: {
      title: "参与方式",
      links: [
        { name: "成为联合发起人", href: "#cofounder" },
        { name: "志愿者招募", href: "#volunteer" },
        { name: "企业合作", href: "#corporate" },
        { name: "捐赠支持", href: "#donate" },
      ],
    },
    resources: {
      title: "资源中心",
      links: [
        { name: "新闻动态", href: "#news" },
        { name: "活动日历", href: "#events" },
        { name: "媒体资料", href: "#media" },
        { name: "常见问题", href: "#faq" },
      ],
    },
  };

  const socialLinks = [
    { icon: Facebook, href: "#", label: "Facebook" },
    { icon: Twitter, href: "#", label: "Twitter" },
    { icon: Instagram, href: "#", label: "Instagram" },
    { icon: Linkedin, href: "#", label: "LinkedIn" },
  ];

  return (
    <footer className="bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <img 
                src={images.logo} 
                alt="CLÉMENT Logo" 
                className="h-8 w-auto"
              />
                <div>
                  <h3 className="text-xl">克来姆公益咖啡与茶</h3>
                  <p className="text-xs text-zinc-400 tracking-widest">Clément Coffee & Tea Philanthropy</p>
                </div>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                用一杯咖啡的温暖，点亮战争儿童的未来。我们致力于为战争地区的儿童提供救援、疗愈和教育支持。
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-zinc-400">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:contact@claimcoffee.org" className="hover:text-amber-500 transition-colors">
                    contact@claimcoffee.org
                  </a>
                </div>
                <div className="flex items-center gap-3 text-sm text-zinc-400">
                  <Phone className="w-4 h-4" />
                  <a href="tel:+86-xxx-xxxx-xxxx" className="hover:text-amber-500 transition-colors">
                    +86 xxx xxxx xxxx
                  </a>
                </div>
                <div className="flex items-center gap-3 text-sm text-zinc-400">
                  <MapPin className="w-4 h-4" />
                  <span>中国 · 上海</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([key, section], index) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <h4 className="text-lg mb-4">{section.title}</h4>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-sm text-zinc-400 hover:text-amber-500 transition-colors"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-zinc-700 to-transparent my-12" />

        {/* Bottom Footer */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Copyright */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-sm text-zinc-500 flex items-center gap-2"
          >
            <span>© 2024 克来姆公益咖啡与茶</span>
            <span>·</span>
            <span>白鸽行动</span>
            <span>·</span>
            <span className="flex items-center gap-1">
              Made with <Heart className="w-4 h-4 text-red-500 fill-red-500" />
            </span>
          </motion.div>

          {/* Social Links */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center gap-4"
          >
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                aria-label={social.label}
                className="w-10 h-10 rounded-full bg-zinc-800 hover:bg-amber-600 flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <social.icon className="w-5 h-5" />
              </a>
            ))}
          </motion.div>

          {/* Legal Links */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex items-center gap-4 text-sm text-zinc-500"
          >
            <a href="#privacy" className="hover:text-amber-500 transition-colors">
              隐私政策
            </a>
            <span>·</span>
            <a href="#terms" className="hover:text-amber-500 transition-colors">
              使用条款
            </a>
          </motion.div>
        </div>
      </div>

      {/* Decorative Bottom Bar */}
      <div className="h-2 bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600" />
    </footer>
  );
}

